// The Guests panel — the Owner's registry of side-door grants (migration 0013).
//
// Lists every guest with its live footprint (sessions + Looks it minted), and drives
// the whole lifecycle: mint (the password appears ONCE, right here, never again),
// rotate (new password shown once; older sessions stop resolving), disable/enable,
// revoke sessions, delete (two-step inline confirm, the house idiom — no window
// dialogs). The lane is owner-only server-side; a guest session never reaches this
// page (the nav hides it and the API answers 403 regardless).

import { useCallback, useEffect, useState } from "react";
import type { ApiClient, GuestCredentials, GuestEntry } from "../api";
import { formatRelative } from "../format";
import { SignedOut } from "./SignedOut";
import { Loading } from "./Loading";
import { ErrorState } from "./ErrorState";
import { EmptyState } from "./EmptyState";
import { ApiError } from "../api";

export interface GuestsPageProps {
  client: ApiClient;
}

export function GuestsPage({ client }: GuestsPageProps): React.JSX.Element {
  const [guests, setGuests] = useState<GuestEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  /** The one-time credentials from the last create/rotate — gone on refresh, by design. */
  const [creds, setCreds] = useState<GuestCredentials | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [liveMsg, setLiveMsg] = useState("");

  const load = useCallback(async () => {
    try {
      setGuests(await client.listGuests());
      setError(null);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 401 || e.status === 403)) setUnauthorized(true);
      else setError(e instanceof Error ? e.message : "Couldn’t load guests.");
    }
  }, [client]);
  useEffect(() => {
    void load();
  }, [load]);

  /** Run one action, refetch, announce — with the button row locked while it flies. */
  async function act(label: string, fn: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await fn();
      await load();
      setLiveMsg(label);
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn’t go through.");
    } finally {
      setBusy(false);
    }
  }

  async function create(): Promise<void> {
    const user = newName.trim().toLowerCase();
    await act(`Guest ${user} created — password shown below, once.`, async () => {
      setCreds(await client.createGuest(user));
      setNewName("");
    });
  }

  if (unauthorized) return <SignedOut />;
  if (error && guests === null) return <ErrorState message={error} onRetry={() => void load()} />;
  if (guests === null) return <Loading label="Loading guests…" />;

  return (
    <section className="guests" data-testid="guests-page">
      <div className="visually-hidden" aria-live="polite" data-testid="guests-live">
        {liveMsg}
      </div>
      <div className="guests__head">
        <h2>Guests</h2>
        <form
          className="guests__create"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          <input
            value={newName}
            // Normalized as typed, so the pattern never blocks a name that only needed lowercasing.
            onChange={(e) => setNewName(e.target.value.toLowerCase())}
            placeholder="new guest name"
            aria-label="New guest name"
            pattern="[a-z0-9][a-z0-9_-]{1,31}"
            title="2-32 chars: a-z, 0-9, - or _"
            required
            data-testid="guest-name-input"
          />
          <button type="submit" className="btn" disabled={busy} data-testid="guest-create">
            Add guest
          </button>
        </form>
      </div>

      {error && (
        <p role="alert" className="guests__meta">
          {error}
        </p>
      )}

      {creds && (
        <div className="guests__creds" role="status" data-testid="guest-credentials">
          <strong>{creds.user}</strong> can now sign in at <code>{`${location.origin}/auth/guest`}</code>
          {" with password "}
          <code data-testid="guest-password">{creds.password}</code>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void (async () => {
                try {
                  await navigator.clipboard.writeText(creds.password);
                  setLiveMsg("Password copied.");
                } catch {
                  setLiveMsg("Copy failed — select the password by hand.");
                }
              })();
            }}
          >
            Copy
          </button>
          <p className="guests__creds-note">
            This password is shown once and stored nowhere — hand it over now, or rotate for a new one.
          </p>
        </div>
      )}

      {guests.length === 0 ? (
        <EmptyState
          title="No guests yet"
          message="Mint a guest to let someone work in your account through the side door — they get your portal, but your Looks stay read-only to them."
        />
      ) : (
        <ul className="guests__list">
          {guests.map((g) => (
            <li key={g.user} className="guests__row" data-testid="guest-row">
              <span className="guests__name">{g.user}</span>
              {g.disabled && (
                <span className="guests__badge" data-testid="guest-disabled-badge">
                  Disabled
                </span>
              )}
              <span className="guests__meta">
                {g.sessions.length === 0
                  ? "no active sessions"
                  : `${g.sessions.length} active session${g.sessions.length === 1 ? "" : "s"} · newest ${formatRelative(g.sessions[0]!.createdAt)}`}
                {" · "}
                {g.looks} look{g.looks === 1 ? "" : "s"}
                {" · added "}
                {formatRelative(g.createdAt)}
              </span>
              <span className="guests__actions">
                {confirmDelete === g.user ? (
                  <>
                    <button
                      type="button"
                      className="btn btn--danger"
                      disabled={busy}
                      onClick={() =>
                        void act(`Guest ${g.user} deleted.`, async () => {
                          await client.deleteGuest(g.user);
                          setConfirmDelete(null);
                          if (creds?.user === g.user) setCreds(null);
                        })
                      }
                      aria-label={`Confirm delete ${g.user}`}
                    >
                      Really delete
                    </button>
                    <button type="button" className="btn" disabled={busy} onClick={() => setConfirmDelete(null)}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() =>
                        void act(`Password for ${g.user} rotated — shown below, once.`, async () => {
                          setCreds(await client.rotateGuest(g.user));
                        })
                      }
                      aria-label={`rotate ${g.user}`}
                    >
                      Rotate password
                    </button>
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() =>
                        void act(
                          g.disabled ? `Guest ${g.user} enabled.` : `Guest ${g.user} disabled.`,
                          () => client.setGuestDisabled(g.user, !g.disabled),
                        )
                      }
                      aria-label={`${g.disabled ? "enable" : "disable"} ${g.user}`}
                    >
                      {g.disabled ? "Enable" : "Disable"}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      disabled={busy || g.sessions.length === 0}
                      onClick={() =>
                        void act(`Signed ${g.user} out everywhere.`, () => client.revokeGuestSessions(g.user))
                      }
                      aria-label={`sign out ${g.user}`}
                    >
                      Sign out everywhere
                    </button>
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      onClick={() => setConfirmDelete(g.user)}
                      aria-label={`delete ${g.user}`}
                    >
                      Delete
                    </button>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
